import * as Logger from 'js-logger';
import {BehaviorSubject, EMPTY, interval, Observable} from 'rxjs';
import {delay, filter, first, tap} from 'rxjs/operators';

import {AbstractPlatform, MenuOrientation} from '@app/platform/platform';
import {ControllerClientServiceBase} from '@app/shared/controller-client.service.base';
import {TimeServiceBase} from '@app/shared/time.service.base';
import {SettingKeys, SettingsService} from '@app/sync/settings.service';
import {SyncConfigDownloadEvent, SyncEndEvent, SyncServiceBase} from '@app/sync/sync.service.base';

export abstract class BootstrapServiceBase {
    readonly dhcpError$: Observable<boolean> = EMPTY;
    readonly messageText$ = new BehaviorSubject<[string, string?]>(['Starte']);
    protected logger = Logger.get('bootstrap');

    protected constructor(
        protected readonly sync: SyncServiceBase,
        protected readonly settings: SettingsService,
        protected readonly platform: AbstractPlatform,
        protected readonly time: TimeServiceBase,
        private readonly controllerClient: ControllerClientServiceBase,
    ) {
    }

    /**
     * Called before anything else after angular has finished booting.
     */
    async bootstrap(): Promise<void> {
        this.logger.debug('Executing bootstrap.service.base.ts:bootstrap()');

        // load our secret
        await this.sync.loadSecret();

        this.sync
            .events
            .pipe(filter(event => event instanceof SyncEndEvent), first())
            .subscribe(this.onAfterFirstSync.bind(this));

        this.sync
            .events
            .pipe(filter(event => event instanceof SyncConfigDownloadEvent))
            .subscribe(this.onConfigDownloaded.bind(this));

        this.scheduleReboot();

        await this.setupNtp();
    }

    /**
     * Called after the first time the sync is run (regardless of whether is succeeded or not).
     */
    protected onAfterFirstSync(): Promise<void> {
        this.logger.debug('Executing bootstrap.service.base.ts:onAfterFirstSync()');
        this.startControllerClient();
        return Promise.resolve();
    }

    /**
     * Called every time a new configuration from the server is received.
     */
    protected async onConfigDownloaded(): Promise<void> {
        this.logger.debug('Executing bootstrap.service.base.ts:onConfigDownloaded()');
        this.startControllerClient();

        await this.setupNtp();

        if (this.platform.isSetMenuOrientationSupported()) {
            const orientation = this.settings.get(SettingKeys.ORIENTATION) === 'SCREEN_ORIENTATION_PORTRAIT'
                ? MenuOrientation.PORTRAIT
                : MenuOrientation.LANDSCAPE;
            try {
                await this.platform.setMenuOrientation(orientation);
            } catch (e) {
                this.logger.error(`Failed to set menu orientation to ${orientation}: ${e.message}`);
            }
        }
    }

    protected startControllerClient(): void {
        const controllerClientEnabled = this.settings.get(SettingKeys.CONTROLLER_CLIENT_ENABLED, false);
        if (controllerClientEnabled && !this.controllerClient.isConnectionStarted) {
            this.controllerClient.connect();
        }
    }

    protected getUptimeForScheduledReboot(): number {
        const uptime = this.platform.getSystemUptime();
        return uptime !== undefined ? uptime : -1;
    }

    private scheduleReboot(): void {
        const rebootHour = 4;  // Do reboot only from 4:00:00 until 4:59:59
        const checkInterval = 60 * 60 * 1000;  // Spread reboot of multiple devices over the reboot hour

        const uptimeThreshold = () => {
            // Use positive infinity to prevent reboot if interval is missing in settings.
            const rebootInterval = this.settings.get(SettingKeys.REBOOT_INTERVAL, Number.POSITIVE_INFINITY) * 1000;

            // Subtract check interval to be sure not to exceed reboot hour especially for daily reboot.
            return rebootInterval - checkInterval;
        };

        // Subtract some seconds from check interval to be sure
        // to match reboot hour with the not so steady time service.
        interval(checkInterval - 5_000)
            .pipe(
                filter(() => this.time.now().hours() === rebootHour),
                filter(() => this.getUptimeForScheduledReboot() >= uptimeThreshold()),
                first(),
                tap(() => this.logger.info('Rebooting as scheduled.')),
                delay(5_000),  // Allow controller client to send the log message above.
            )
            .subscribe(async () => this.platform.reboot());
    }

    private async setupNtp(): Promise<void> {
        if (!this.platform.isNtpSettingsAccessSupported()) {
            return;
        }

        try {
            await this.platform.setNtpConfig({
                enabled: true,
                timeZone: this.settings.get(SettingKeys.TIME_ZONE, 'Europe/Amsterdam'),
                serverAddress: 'pool.ntp.org',
            });
        } catch (e) {
            this.logger.error(`Failed to set ntp configuration: ${e.message}`);
        }
    }
}
